import { CircularProgress, Dialog, DialogBody, DialogContent, DialogFooter } from '@civilgrid/ui/components';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { type MapRef, useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Button2 from '~/components2/forms/Button';
import TextArea from '~/components2/forms/TextArea';
import type { MapStoreBase } from '~/components2/Map';
import { Notify } from '~/components2/Notify';
import { useReportProblem } from '~/hooks/useReportProblem';
import { useReportProblemEnabled } from '~/hooks/useReportProblemEnabled';
import layerStore from '~/store2/layers';
import { TOCLabelMap } from '~/store2/TOCLayers';
import { captureMapCanvas } from '../../helpers/captureMapCanvas';
import { drawReferencePin, type ScreenshotReferencePoint } from '../../helpers/drawReferencePin';

interface Props {
  store: MapStoreBase;
  projectId?: string;
}

const MAX_SCREENSHOT_DIMENSION = 1600;
const MAX_SCREENSHOT_BYTES = 3 * 1024 * 1024;
const MAX_REPORT_PROBLEM_DESCRIPTION_LENGTH = 5000;

function captureMapAsBase64(map: MapRef): Promise<string> {
  const mapCanvas = map.getCanvas();
  const scale = Math.min(1, MAX_SCREENSHOT_DIMENSION / Math.max(mapCanvas.width, mapCanvas.height));
  const width = Math.round(mapCanvas.width * scale);
  const height = Math.round(mapCanvas.height * scale);

  const canvas = new OffscreenCanvas(width, height);
  return captureMapCanvas(map, canvas, (src, ctx) => ctx.drawImage(src, 0, 0, width, height), 'image/jpeg', {
    quality: 0.9,
    maxSizeBytes: MAX_SCREENSHOT_BYTES,
    minQuality: 0.5,
  });
}

export default function ReportProblem({ store, projectId }: Props) {
  const { current: map } = useMap();
  const { reportProblemPoint } = useSnapshot(store);
  const { visibleLayerIds } = useSnapshot(layerStore) as typeof layerStore;
  const [screenshot, setScreenshot] = useState('');
  const [captureFailed, setCaptureFailed] = useState(false);
  const [referencePoint, setReferencePoint] = useState<ScreenshotReferencePoint | null>(null);
  const reportProblemEnabled = useReportProblemEnabled();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange', defaultValues: { description: '' } });

  const reportProblemMutation = useReportProblem();

  const closeModal = () => {
    store.setReportProblemPoint(undefined);
    setScreenshot('');
    setCaptureFailed(false);
    setReferencePoint(null);
    reset();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-capture when a new point is set
  useEffect(() => {
    if (!reportProblemPoint || !map) return;

    let cancelled = false;
    setScreenshot('');
    setCaptureFailed(false);

    // Reference point is fixed to where the "Request Data Review" context menu was triggered on the map
    const canvasRect = map.getCanvas().getBoundingClientRect();
    const projected = map.project([reportProblemPoint.lng, reportProblemPoint.lat]);
    setReferencePoint({
      xRatio: projected.x / canvasRect.width,
      yRatio: projected.y / canvasRect.height,
    });

    captureMapAsBase64(map)
      .then((result) => {
        if (!cancelled) setScreenshot(result);
      })
      .catch(() => {
        if (!cancelled) setCaptureFailed(true);
      });
    capturePosthogEvent(POSTHOG_EVENTS.reportProblemModalOpened, { project_id: projectId });

    return () => {
      cancelled = true;
    };
  }, [reportProblemPoint]);

  const onSubmit = handleSubmit(async ({ description }) => {
    if (!reportProblemPoint || !map) return;

    const layers = Array.from(visibleLayerIds).map((id) => TOCLabelMap.get(id) ?? id);

    try {
      const screenshotBase64 =
        screenshot && referencePoint ? await drawReferencePin(screenshot, referencePoint) : screenshot;
      const result = await reportProblemMutation.mutateAsync({
        description,
        screenshotBase64,
        coords: { lng: reportProblemPoint.lng, lat: reportProblemPoint.lat, zoom: map.getZoom() },
        projectId: projectId ?? '',
        projectLink: window.location.href,
        layers,
        posthogSessionUrl: posthog.get_session_replay_url?.() ?? null,
        metadata: {
          userAgent: navigator.userAgent,
          viewport: { width: window.innerWidth, height: window.innerHeight },
        },
      });

      capturePosthogEvent(POSTHOG_EVENTS.reportProblemSubmitted, {
        project_id: projectId,
        issue_identifier: result.issueIdentifier,
      });

      Notify.info({
        title: 'Data review request submitted',
        message: 'Thanks for submitting your data review request. Someone from CivilGrid will look into it.',
      });
      closeModal();
    } catch {
      Notify.error({
        title: 'Error Reporting Problem',
        message: 'There was an error submitting your report. Please try again or contact support.',
      });
    }
  });

  if (!reportProblemPoint || !reportProblemEnabled) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) closeModal();
      }}
    >
      <DialogContent
        title="Request Data Review"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="report-problem-dialog"
        // Opened from the map's context menu, whose item is gone by close, so focus returns to the map.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          map?.getCanvas().focus();
        }}
      >
        <DialogBody className="w-[28rem] max-w-full">
          <p className="mb-3 text-xs text-primary-dark">
            Let us know if you have found data on the map that needs to be reviewed. Describe the issue below and
            someone from CivilGrid will look into it on your behalf.
          </p>

          <p className="mb-2 font-mono text-xs font-semibold" data-testid="report-problem-coordinates-label">
            {reportProblemPoint.lat.toFixed(6)}, {reportProblemPoint.lng.toFixed(6)}
          </p>

          {captureFailed ? (
            <p className="mb-3 text-xs text-primary-dark" data-testid="report-problem-screenshot-error">
              Couldn't capture a map screenshot. You can still submit a description.
            </p>
          ) : screenshot === '' ? (
            <div className="mb-3 flex h-48 items-center justify-center">
              <CircularProgress indeterminate />
            </div>
          ) : (
            <div className="mb-3 flex justify-center">
              <div className="relative">
                <img
                  src={screenshot}
                  alt="Map screenshot"
                  className="max-h-[19.5rem] max-w-full rounded-lg border border-grey-30"
                  data-testid="report-problem-screenshot"
                />
                {referencePoint && (
                  <div
                    className="-translate-x-1/2 -translate-y-1/2 absolute size-4 rounded-full border-2 border-white bg-red-500 shadow"
                    style={{ left: `${referencePoint.xRatio * 100}%`, top: `${referencePoint.yRatio * 100}%` }}
                    data-testid="report-problem-reference-pin"
                  />
                )}
              </div>
            </div>
          )}

          <Controller
            name="description"
            control={control}
            rules={{
              required: 'A description is required',
              maxLength: {
                value: MAX_REPORT_PROBLEM_DESCRIPTION_LENGTH,
                message: `Must be ${MAX_REPORT_PROBLEM_DESCRIPTION_LENGTH} characters or fewer`,
              },
            }}
            render={({ field: { ref, ...rest } }) => (
              <TextArea
                label="Describe what needs review"
                isRequired
                error={errors.description?.message}
                placeholder="Give us details about the area that needs review, the data layer, an issue on the map, etc."
                data-testid="report-problem-description"
                {...rest}
              />
            )}
          />
          {errors.description && (
            <p className="mt-1 text-xs text-red-500" data-testid="report-problem-description-error">
              {errors.description.message}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button2 alternate data-testid="report-problem-cancel" onClick={closeModal}>
            Cancel
          </Button2>
          <Button2
            data-testid="report-problem-submit"
            className="h-7.5"
            onClick={onSubmit}
            disabled={(screenshot === '' && !captureFailed) || !isValid || reportProblemMutation.isPending}
            isProcessing={reportProblemMutation.isPending}
          >
            Submit
          </Button2>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
